import { patientDisplayName } from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

export function buildConditionInsightCharts(
  conditions: fhir5.Condition[],
  verificationLabel: (code: string) => string
) {
  const statusMap = new Map<string, number>();
  for (const cond of conditions) {
    const key = cond.verificationStatus?.coding?.[0]?.code || "unknown";
    statusMap.set(key, (statusMap.get(key) || 0) + 1);
  }

  const codeCounts = new Map<string, { label: string; value: number }>();
  for (const cond of conditions) {
    const codingWithCode = cond.code?.coding?.find((c) => !!c.code);
    const code = codingWithCode?.code || "Não informado";
    const label = codingWithCode?.display || cond.code?.text || code;
    const prev = codeCounts.get(code);
    codeCounts.set(code, { label, value: (prev?.value ?? 0) + 1 });
  }

  const statusItems: InsightChartItem[] = Array.from(statusMap.entries())
    .map(([key, value]) => ({
      key,
      label: verificationLabel(key) || key,
      value,
    }))
    .sort((a, b) => b.value - a.value);

  const rankingItems: InsightChartItem[] = Array.from(codeCounts.entries())
    .map(([key, { label, value }]) => ({ key, label, value }))
    .sort((a, b) => b.value - a.value);

  return { statusItems, rankingItems, total: conditions.length };
}

export function buildConditionPatientRows(
  conditions: fhir5.Condition[],
  patients: fhir5.Patient[],
  verificationLabel: (code: string) => string
): InsightPatientRow[] {
  const patientMap = new Map<string, fhir5.Patient>();
  for (const patient of patients) {
    if (patient.id) patientMap.set(patient.id, patient);
  }

  const groups = new Map<string, InsightPatientRow>();
  for (const cond of conditions) {
    const patientRef = cond.subject?.reference;
    if (!patientRef) continue;
    const patientId = patientRef.split("/").pop() || "";
    if (!patientId) continue;

    const codingWithCode = cond.code?.coding?.find((c) => !!c.code);
    const code = codingWithCode?.code || "Não informado";
    const label = codingWithCode?.display || cond.code?.text || code;
    const statusKey = cond.verificationStatus?.coding?.[0]?.code || "unknown";
    const date = cond.recordedDate
      ? new Date(cond.recordedDate).toLocaleDateString()
      : "Não informado";

    if (!groups.has(patientId)) {
      groups.set(patientId, {
        patientId,
        patientName: patientDisplayName(patientMap.get(patientId)),
        patient: patientMap.get(patientId),
        items: [],
      });
    }
    groups.get(patientId)!.items.push({
      itemKey: code,
      label,
      statusKey,
      statusLabel: verificationLabel(statusKey) || statusKey,
      practitioner: "Não informado",
      date,
    });
  }

  return Array.from(groups.values()).sort((a, b) => b.items.length - a.items.length);
}
